/**
 * Training: my certification and progress toward recertification (SC MRN), classes to sign up for,
 * and certificates to send the training officer. The training officer's side: everyone's status,
 * entering training records, filing certificates, scheduling classes and taking attendance, the MRN.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { CERT_CLASSES, CERT_CLASS_KEYS, QUAL_COURSES, TRAINING_STATUS_LABEL } from '@civilos/domain';
import { api, fileToBase64 } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, FileDrop, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, KPI, PeoplePicker } from './common.tsx';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
const STATUS_TONE: Record<string, Tone> = { complete: 'ok', on_track: 'ok', due_soon: 'warn', behind: 'bad', expired: 'bad', not_set: 'neutral' };
const ITEM: Record<string, { label: string; tone: Tone; icon: string }> = {
  done: { label: 'Done', tone: 'ok', icon: '✓' },
  made_up: { label: 'Made up', tone: 'ok', icon: '✓' },
  due: { label: 'Due this year', tone: 'warn', icon: '•' },
  missed: { label: 'Missed — make-up needed', tone: 'bad', icon: '!' },
  upcoming: { label: 'Later', tone: 'neutral', icon: '–' },
};
const d = (iso: string | null | undefined) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const dt = (iso: string) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const localToday = () => new Date().toLocaleDateString('en-CA');
const catName = (cats: any[], k: string | null | undefined) => cats.find((c) => c.key === k)?.name ?? k ?? '—';
const hrs = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 })} h`);

// ─── Progress (shared by "My training" and the training officer's view of one officer) ──────────
function Progress({ v }: { v: any }) {
  const p = v.progress;
  const cats = v.categories;
  if (p.status === 'not_set')
    return (
      <Notice tone="warn">
        Your certification class and renewal date aren’t set up yet. {v.canManage ? 'Set them below.' : 'The training officer sets them up — ask them if this is wrong.'}
      </Notice>
    );
  const cur = p.years.find((y: any) => y.when === 'current');
  return (
    <div className="stack">
      <div className="tr-cert card">
        <div className="tr-ring" style={{ ['--pct' as string]: `${p.percent}` }} role="img" aria-label={`${p.percent}% toward recertification`}>
          <strong>{p.percent}%</strong>
          <span>toward recert</span>
        </div>
        <div className="tr-cert-info">
          <div className="row gap wrap">
            <h2>{p.classLabel}</h2>
            <Badge tone={STATUS_TONE[p.status]}>{TRAINING_STATUS_LABEL[p.status]}</Badge>
          </div>
          <dl className="tr-facts">
            <div>
              <dt>Renewal date</dt>
              <dd>{d(p.period.end)}</dd>
            </div>
            <div>
              <dt>{p.daysLeft >= 0 ? 'Days left' : 'Days past'}</dt>
              <dd>{Math.abs(p.daysLeft).toLocaleString()}</dd>
            </div>
            <div>
              <dt>Academy ID</dt>
              <dd>{v.profile?.academy_id ?? '—'}</dd>
            </div>
            <div>
              <dt>Certification period</dt>
              <dd>
                {d(p.period.start)} – {d(p.period.end)}
              </dd>
            </div>
          </dl>
          {cur && p.dueNow.length > 0 && (
            <p className="small">
              Still to do this year (by {d(cur.end)}): <strong>{p.dueNow.map((k: string) => catName(cats, k)).join(', ')}</strong>
            </p>
          )}
          {p.missed.length > 0 && (
            <p className="small tr-bad">
              Missed: <strong>{p.missed.map((k: string) => catName(cats, k)).join(', ')}</strong> — a make-up is needed.
            </p>
          )}
        </div>
      </div>

      <div className="tr-years">
        {p.years.map((y: any) => (
          <section key={y.n} className={`card tr-year ${y.when}`} aria-label={`Year ${y.n}`}>
            <header>
              <strong>Year {y.n}</strong>
              <span className="small muted">
                {d(y.start)} – {d(y.end)}
              </span>
              {y.when === 'current' && <Badge tone="info">This year</Badge>}
            </header>
            <ul>
              {y.items.map((i: any) => (
                <li key={i.key} className={`tr-item ${i.status}`}>
                  <span className="tr-dot" aria-hidden>
                    {ITEM[i.status]!.icon}
                  </span>
                  <span className="tr-item-name">{catName(cats, i.key)}</span>
                  <span className="small muted">{i.record ? `${d(i.record.completed_on)}${i.status === 'made_up' ? ' (make-up)' : ''}` : ITEM[i.status]!.label}</span>
                </li>
              ))}
              {y.items.length === 0 && <li className="small muted">No yearly topics for this class.</li>}
            </ul>
            <footer className="small muted">{hrs(y.hours)} of training this year</footer>
          </section>
        ))}
      </div>

      <div className="grid2">
        <Card title="In-service hours (3 years)">
          {p.hours.need ? (
            <>
              <div className="tr-bar" role="progressbar" aria-valuemin={0} aria-valuemax={p.hours.need} aria-valuenow={p.hours.done}>
                <span style={{ width: `${Math.min(100, (p.hours.done / p.hours.need) * 100)}%` }} />
              </div>
              <p className="small">
                <strong>{hrs(p.hours.done)}</strong> of {p.hours.need} hours{p.hours.done >= p.hours.need ? ' — met' : ` — ${hrs(Math.max(0, p.hours.need - p.hours.done))} to go`}
              </p>
            </>
          ) : (
            <p className="small">
              <strong>{hrs(p.hours.done)}</strong> so far. {p.classLabel} has no hour total on the MRN.
            </p>
          )}
        </Card>
        <Card title="Mental health awareness (once in 3 years)">
          {p.mentalHealth ? (
            <p className="small">
              <Badge tone="ok">Done</Badge> {p.mentalHealth.title}, {d(p.mentalHealth.completed_on)}
            </p>
          ) : (
            <p className="small">
              <Badge tone="warn">Not yet</Badge> One academy-approved mental health or addictive disorders credit is needed before {d(p.period.end)}.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

function RecordsTable({ v, onVoid }: { v: any; onVoid?: (id: string) => void }) {
  const cats = v.categories;
  if (!v.records.length) return <EmptyState icon="book" title="No training on file yet" />;
  return (
    <div className="card table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Category</th>
            <th>Training</th>
            <th>Hours</th>
            <th>Result</th>
            <th>Entered by</th>
            {onVoid && <th />}
          </tr>
        </thead>
        <tbody>
          {v.records.map((r: any) => (
            <tr key={r.id} className={r.voided_at ? 'tr-void' : ''}>
              <td>{d(r.completed_on)}</td>
              <td>{catName(cats, r.category)}</td>
              <td>
                {r.title}
                {r.makeup_for && <span className="small muted"> · make-up</span>}
                {r.acadis && <span className="small muted"> · ACADIS</span>}
                {r.separate_cert && <span className="small muted"> · separate certification</span>}
                {r.voided_at && <div className="small tr-bad">Voided: {r.void_reason}</div>}
              </td>
              <td>{hrs(r.hours)}</td>
              <td>{r.result ?? ''}</td>
              <td className="small muted">{r.entered_by_name}</td>
              {onVoid && <td>{!r.voided_at && <button type="button" className="btn link small" onClick={() => onVoid(r.id)}>Void</button>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── My training ────────────────────────────────────────────────────────────
export function MyTraining() {
  const { navigate } = useRouter();
  const tabs = ['progress', 'classes', 'certificates'] as const;
  const want = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState<(typeof tabs)[number]>((tabs as readonly string[]).includes(want ?? '') ? (want as any) : 'progress');
  const r = useLoad(() => api.get<any>('/api/training/me'), []);
  const v = r.data;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Training"
        title="My training"
        sub="Your certification, progress toward recertification, classes to sign up for, and certificates for the training officer."
        actions={
          v?.canManage && (
            <>
              {v.pendingReview > 0 && (
                <button className="btn" onClick={() => navigate(at('/training/review'))}>
                  Certificates to file ({v.pendingReview})
                </button>
              )}
              <button className="btn primary" onClick={() => navigate(at('/training/officers'))}>
                Training officer
              </button>
            </>
          )
        }
      />
      <div className="seg-ctl" role="tablist" aria-label="Training">
        {tabs.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
            {{ progress: 'My progress', classes: 'Upcoming classes', certificates: 'Send a certificate' }[k]}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !v ? (
        <Spinner />
      ) : tab === 'progress' ? (
        <>
          <Progress v={v} />
          <FirearmsCard v={v} id={null} onSaved={r.reload} />
          <h2 className="tr-h">Training on file</h2>
          <RecordsTable v={v} />
        </>
      ) : tab === 'classes' ? (
        <ClassList sessions={v.sessions} cats={v.categories} onChange={r.reload} />
      ) : (
        <Certificates v={v} onChange={r.reload} />
      )}
    </div>
  );
}

function ClassList({ sessions, cats, onChange }: { sessions: any[]; cats: any[]; onChange: () => void }) {
  const a = useAction();
  const { navigate } = useRouter();
  if (!sessions.length) return <EmptyState icon="calendar" title="No classes scheduled">When the training officer schedules a class, it shows up here to sign up for.</EmptyState>;
  const go = (id: string, cancel: boolean) => a.run(async () => (await api.post(`/api/training/sessions/${id}/signup`, { cancel }), onChange()));
  return (
    <div className="stack">
      <ErrorBox error={a.error} />
      <div className="tr-classes">
        {sessions.map((s) => {
          const full = s.seats != null && s.taken >= s.seats && s.my_status !== 'signed_up';
          const started = +new Date(s.starts_at) <= Date.now();
          return (
            <article key={s.id} className="card tr-class">
              <div className="tr-class-when">
                <span>{new Date(s.starts_at).toLocaleDateString('en-US', { month: 'short' })}</span>
                <strong>{new Date(s.starts_at).getDate()}</strong>
              </div>
              <div className="tr-class-body">
                <button type="button" className="btn link tr-class-title" onClick={() => navigate(at(`/training/classes/${s.id}`))}>
                  {s.title}
                </button>
                <div className="small muted">
                  {catName(cats, s.category)} · {dt(s.starts_at)} · {hrs(s.hours)}
                  {s.location ? ` · ${s.location}` : ''}
                </div>
                {s.instructor && <div className="small muted">Instructor: {s.instructor}</div>}
                {s.description && <p className="small">{s.description}</p>}
                <div className="small">{s.seats != null ? `${Math.max(0, s.seats - s.taken)} of ${s.seats} seats left` : `${s.taken} signed up`}</div>
              </div>
              <div className="tr-class-act">
                {s.my_status === 'signed_up' ? (
                  <>
                    <Badge tone="ok">You’re signed up</Badge>
                    {!started && (
                      <button type="button" className="btn small" disabled={a.busy} onClick={() => go(s.id, true)}>
                        Drop
                      </button>
                    )}
                  </>
                ) : started ? (
                  <Badge>Started</Badge>
                ) : full ? (
                  <Badge tone="warn">Full</Badge>
                ) : (
                  <button type="button" className="btn primary" disabled={a.busy} onClick={() => go(s.id, false)}>
                    Sign up
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function Certificates({ v, onChange }: { v: any; onChange: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [f, setF] = useState({ title: '', completedOn: '', hours: '', category: '', note: '' });
  const [sent, setSent] = useState(false);
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file) return a.setError(new Error('Choose the certificate file (PDF or a photo).'));
    a.run(async () => {
      await api.post('/api/training/submissions', {
        fileBase64: await fileToBase64(file),
        filename: file.name,
        title: f.title,
        completedOn: f.completedOn || null,
        hours: f.hours ? Number(f.hours) : null,
        category: f.category || null,
        note: f.note || null,
      });
      setFile(null);
      setF({ title: '', completedOn: '', hours: '', category: '', note: '' });
      setSent(true);
      onChange();
    });
  };
  const ST: Record<string, [string, Tone]> = { pending: ['Waiting for the training officer', 'warn'], filed: ['Filed', 'ok'], returned: ['Sent back', 'bad'] };
  return (
    <div className="grid2 top">
      <Card title="Send a certificate to the training officer">
        <form className="stack" onSubmit={submit}>
          <p className="small muted">Took training somewhere else? Upload the certificate. The training officer checks it and files it under the right category.</p>
          {sent && <Notice tone="ok">Sent. You’ll get a message when it’s filed.</Notice>}
          <ErrorBox error={a.error} />
          <FileDrop accept="application/pdf,image/jpeg,image/png" file={file} onFile={(x) => (setFile(x), setSent(false))} label="Certificate file" hint="PDF or a photo of the certificate (JPEG or PNG), 15 MB max" />
          <label>
            What was the training?
            <input value={f.title} onChange={set('title')} required maxLength={200} placeholder="e.g. Crisis Intervention Team, 40 hr" />
          </label>
          <div className="grid-3">
            <label>
              Date completed
              <input type="date" value={f.completedOn} max={localToday()} onChange={set('completedOn')} />
            </label>
            <label>
              Hours
              <input type="number" min={0} max={400} step={0.5} value={f.hours} onChange={set('hours')} />
            </label>
            <label>
              Category (your best guess)
              <select value={f.category} onChange={set('category')}>
                <option value="">Not sure</option>
                {v.categories
                  .filter((c: any) => !c.archived)
                  .map((c: any) => (
                    <option key={c.key} value={c.key}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <label>
            Note for the training officer
            <textarea rows={2} value={f.note} onChange={set('note')} maxLength={1000} />
          </label>
          <div>
            <button className="btn primary" disabled={a.busy}>
              {a.busy ? 'Sending…' : 'Send certificate'}
            </button>
          </div>
        </form>
      </Card>
      <Card title="Certificates you’ve sent">
        {!v.submissions.length ? (
          <p className="small muted">None yet.</p>
        ) : (
          <ul className="tr-subs">
            {v.submissions.map((s: any) => (
              <li key={s.id}>
                <div>
                  <a href={`/api/training/submissions/${s.id}/file`} target="_blank" rel="noreferrer">
                    {s.title}
                  </a>
                  <div className="small muted">
                    Sent {d(s.created_at)}
                    {s.completed_on ? ` · completed ${d(s.completed_on)}` : ''}
                    {s.hours ? ` · ${hrs(s.hours)}` : ''}
                    {s.status === 'filed' && s.category ? ` · ${catName(v.categories, s.category)}` : ''}
                  </div>
                  {s.status === 'returned' && s.review_note && <div className="small tr-bad">“{s.review_note}”</div>}
                </div>
                <Badge tone={ST[s.status]?.[1] ?? 'neutral'}>{ST[s.status]?.[0] ?? s.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ─── Training officer: everyone ─────────────────────────────────────────────
export function TrainingOfficers() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [entering, setEntering] = useState(false);
  const [cats2, setCats2] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/training/officers?q=${encodeURIComponent(q.trim())}&status=${status}`), [q, status]);
  const all = useLoad(() => api.get<any>('/api/training/officers'), [entering]);
  const c = r.data?.counts;
  const cats = useLoad(() => api.get<any>('/api/training/categories'), []);
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<button className="btn link" onClick={() => navigate(at('/training'))}>Training</button>}
        title="Training officer"
        sub="Everyone’s certification and where they stand for recertification. Most urgent first."
        actions={
          r.data?.canManage && (
            <>
              <button className="btn" onClick={() => navigate(at('/training/review'))}>
                Certificates to file{r.data.pending ? ` (${r.data.pending})` : ''}
              </button>
              <button className="btn" onClick={() => navigate(at('/training/classes'))}>
                Classes
              </button>
              <button className="btn" onClick={() => setCats2(!cats2)}>
                Categories
              </button>
              <button className="btn" onClick={() => setEntering(!entering)}>
                Enter training
              </button>
              <button className="btn" onClick={() => navigate(at('/training/quals'))}>
                Log firearms qual
              </button>
              <button className="btn primary" onClick={() => navigate(at('/training/roster'))}>
                Log training roster
              </button>
            </>
          )
        }
      />
      {cats2 && <CategoriesCard onClose={() => (setCats2(false), cats.reload(), r.reload())} />}
      {entering && cats.data && all.data && <EnterTraining cats={cats.data.categories} people={all.data.officers} onDone={() => (setEntering(false), r.reload())} onCancel={() => setEntering(false)} />}
      {c && (
        <div className="ops-kpis">
          {(['expired', 'behind', 'due_soon', 'not_set', 'on_track', 'complete'] as const).map((k) => (
            <KPI key={k} n={c[k] ?? 0} label={TRAINING_STATUS_LABEL[k]!} tone={k === 'expired' || k === 'behind' ? (c[k] ? 'bad' : undefined) : k === 'due_soon' ? (c[k] ? 'warn' : undefined) : undefined} on={status === k} onClick={() => setStatus(status === k ? '' : k)} />
          ))}
        </div>
      )}
      {r.data?.fqDeclines > 0 && (
        <Notice tone="warn">
          ⚠ {r.data.fqDeclines} officer{r.data.fqDeclines === 1 ? '’s' : 's’'} firearms score{r.data.fqDeclines === 1 ? ' has' : 's have'} dropped two quals in a row — marked “Scores dropping” below.
        </Notice>
      )}
      <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, badge, academy ID…" aria-label="Search officers" />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.officers.length ? (
        <EmptyState icon="people" title="No one matches" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Officer</th>
                <th>Class</th>
                <th>Renewal</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Qual avg (day / night)</th>
                <th>Needs</th>
              </tr>
            </thead>
            <tbody>
              {r.data.officers.map((o: any) => (
                <tr key={o.id} className="clickable" onClick={() => navigate(at(`/training/officers/${o.id}`))}>
                  <td>
                    <strong>{o.name}</strong>
                    <div className="small muted">{[o.badge, o.academyId && `Academy ${o.academyId}`].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td>{o.classLabel ?? '—'}</td>
                  <td>
                    {d(o.renewalDate)}
                    {o.daysLeft != null && <div className="small muted">{o.daysLeft >= 0 ? `${o.daysLeft} days` : `${-o.daysLeft} days ago`}</div>}
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[o.status]}>{TRAINING_STATUS_LABEL[o.status]}</Badge>
                    {o.pending > 0 && <div className="small">{o.pending} certificate{o.pending > 1 ? 's' : ''} to file</div>}
                  </td>
                  <td style={{ minWidth: 120 }}>
                    {o.status !== 'not_set' && (
                      <>
                        <div className="tr-bar sm">
                          <span style={{ width: `${o.percent}%` }} />
                        </div>
                        <span className="small muted">{o.percent}%</span>
                      </>
                    )}
                  </td>
                  <td className="small">
                    {o.fq?.overall != null ? (
                      <>
                        <strong>{pct(o.fq.overall)}</strong>
                        <div className="muted">
                          {pct(o.fq.day)} / {pct(o.fq.night)}
                        </div>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                    <QualWarn fq={o.fq} />
                  </td>
                  <td className="small">
                    {o.missed.length > 0 && <div className="tr-bad">Missed: {o.missed.map((k: string) => catName(cats.data?.categories ?? [], k)).join(', ')}</div>}
                    {o.dueNow.length > 0 && <div>This year: {o.dueNow.map((k: string) => catName(cats.data?.categories ?? [], k)).join(', ')}</div>}
                    {o.hours?.need && o.hours.done < o.hours.need ? <div className="muted">{hrs(o.hours.need - o.hours.done)} to go</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Enter one training for one officer or a whole class at once. */
function EnterTraining({ cats, people, fixed, onDone, onCancel }: { cats: any[]; people: any[]; fixed?: string; onDone: () => void; onCancel: () => void }) {
  const [who, setWho] = useState<string[]>(fixed ? [fixed] : []);
  const [f, setF] = useState({ category: '', title: '', completedOn: localToday(), hours: '', result: '', instructor: '', location: '', acadis: false, separateCert: false, makeupFor: '', notes: '' });
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const preset: Record<string, string> = { firearms: 'Annual firearms qualification', less_lethal: 'Less-lethal weapons in-service', use_of_force: 'Use of force policy review', evo: 'EVO in-service', dv: 'Domestic violence in-service', legal: 'Legal update', mental_health: 'Mental health awareness', ground_defense: 'Ground defense' };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post('/api/training/records', { ...f, userIds: who, hours: Number(f.hours || 0), makeupFor: f.makeupFor || null, result: f.result || null, instructor: f.instructor || null, location: f.location || null, notes: f.notes || null });
      onDone();
    });
  };
  return (
    <Card title={fixed ? 'Enter training' : 'Enter training (one officer or a whole class)'}>
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={a.error} />
        {!fixed && <PeoplePicker people={people.map((p) => ({ id: p.id, name: p.name, title: p.title }))} value={who} onChange={setWho} label="Officers" placeholder="Type a name…" />}
        <div className="grid-3">
          <label>
            Category
            <select value={f.category} required onChange={(e) => setF({ ...f, category: e.target.value, title: f.title || preset[e.target.value] || '' })}>
              <option value="">Choose…</option>
              {cats
                .filter((c) => !c.archived)
                .map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Training
            <input value={f.title} onChange={set('title')} required maxLength={200} />
          </label>
          <label>
            Date
            <input type="date" value={f.completedOn} max={localToday()} onChange={set('completedOn')} required />
          </label>
          <label>
            Hours
            <input type="number" min={0} max={400} step={0.5} value={f.hours} onChange={set('hours')} required />
          </label>
          <label>
            Result / score
            <input value={f.result} onChange={set('result')} placeholder="e.g. Qualified 94%" maxLength={120} />
          </label>
          <label>
            Instructor
            <input value={f.instructor} onChange={set('instructor')} maxLength={120} />
          </label>
          <label>
            Location
            <input value={f.location} onChange={set('location')} maxLength={200} />
          </label>
          <label>
            Make-up for a missed year (optional)
            <input type="date" value={f.makeupFor} onChange={set('makeupFor')} title="Any date in the year it makes up for" />
          </label>
        </div>
        <div className="row gap wrap">
          <label className="check">
            <input type="checkbox" checked={f.acadis} onChange={(e) => setF({ ...f, acadis: e.target.checked })} /> On ACADIS
          </label>
          <label className="check">
            <input type="checkbox" checked={f.separateCert} onChange={(e) => setF({ ...f, separateCert: e.target.checked })} /> Separate certification (CJA class that grants a certification)
          </label>
        </div>
        <div className="row gap">
          <button className="btn primary" disabled={a.busy || !who.length}>
            {a.busy ? 'Saving…' : who.length > 1 ? `Save for ${who.length} officers` : 'Save'}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

// ─── Training officer: one officer ──────────────────────────────────────────
export function TrainingOfficer({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/training/officers/${id}`), [id]);
  const [entering, setEntering] = useState(false);
  const a = useAction();
  const v = r.data;
  const voidIt = (rid: string) => {
    const reason = prompt('Why is this record being voided?');
    if (reason) a.run(async () => (await api.post(`/api/training/records/${rid}/void`, { reason }), r.reload()));
  };
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<button className="btn link" onClick={() => navigate(at('/training/officers'))}>Training officer</button>}
        title={v ? v.officer.name : 'Training'}
        sub={v ? [v.officer.title, v.officer.badge].filter(Boolean).join(' · ') : undefined}
        actions={
          v && (
            <>
              {v.progress.status !== 'not_set' && (
                <button className="btn" onClick={() => navigate(at(`/training/officers/${id}/mrn`))}>
                  MRN
                </button>
              )}
              {v.canManage && (
                <button className="btn primary" onClick={() => setEntering(!entering)}>
                  Enter training
                </button>
              )}
            </>
          )
        }
      />
      <ErrorBox error={r.error ?? a.error} />
      {!v ? (
        !r.error && <Spinner />
      ) : (
        <>
          {entering && <EnterTraining cats={v.categories} people={[]} fixed={id} onDone={() => (setEntering(false), r.reload())} onCancel={() => setEntering(false)} />}
          <Progress v={v} />
          <FirearmsCard v={v} id={id} onSaved={r.reload} />
          {v.canManage && <ProfileForm id={id} v={v} onSaved={r.reload} />}
          <h2 className="tr-h">Training on file</h2>
          <RecordsTable v={v} onVoid={v.canManage ? voidIt : undefined} />
          {v.submissions.length > 0 && (
            <Card title="Certificates they’ve sent">
              <ul className="tr-subs">
                {v.submissions.map((s: any) => (
                  <li key={s.id}>
                    <a href={`/api/training/submissions/${s.id}/file`} target="_blank" rel="noreferrer">
                      {s.title}
                    </a>
                    <Badge tone={s.status === 'filed' ? 'ok' : s.status === 'pending' ? 'warn' : 'bad'}>{s.status === 'pending' ? 'To file' : s.status === 'filed' ? 'Filed' : 'Sent back'}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function ProfileForm({ id, v, onSaved }: { id: string; v: any; onSaved: () => void }) {
  const p = v.profile ?? {};
  const [f, setF] = useState({ academyId: p.academy_id ?? '', certClass: p.cert_class ?? '', renewalDate: p.renewal_date ?? '', firearmsIssued: p.firearms_issued ?? true, lessLethalIssued: p.less_lethal_issued ?? true, notes: p.notes ?? '' });
  const [saved, setSaved] = useState(false);
  const a = useAction();
  const save = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post(`/api/training/officers/${id}/profile`, { ...f, certClass: f.certClass || null, renewalDate: f.renewalDate || null, academyId: f.academyId || null, notes: f.notes || null });
      setSaved(true);
      onSaved();
    });
  };
  const renewed = () => {
    if (confirm('Mark recertified? The next renewal date will be three years later.')) a.run(async () => (await api.post(`/api/training/officers/${id}/renewed`, {}), onSaved()));
  };
  return (
    <Card
      title="Certification"
      actions={
        p.renewal_date && (
          <button type="button" className="btn" onClick={renewed} disabled={a.busy}>
            Recertified — start next cycle
          </button>
        )
      }
    >
      <form className="stack" onSubmit={save}>
        <ErrorBox error={a.error} />
        {saved && <Notice tone="ok">Saved.</Notice>}
        <div className="grid-3">
          <label>
            Certification class
            <select value={f.certClass} onChange={(e) => setF({ ...f, certClass: e.target.value, firearmsIssued: e.target.value === 'class2' ? false : f.firearmsIssued })}>
              <option value="">Not set</option>
              {CERT_CLASS_KEYS.map((k) => (
                <option key={k} value={k}>
                  {CERT_CLASSES[k].label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Certification renewal date
            <input type="date" value={f.renewalDate} onChange={(e) => setF({ ...f, renewalDate: e.target.value })} />
          </label>
          <label>
            Academy ID
            <input value={f.academyId} onChange={(e) => setF({ ...f, academyId: e.target.value })} placeholder="####-####" maxLength={9} />
          </label>
        </div>
        <div className="row gap wrap">
          <label className="check">
            <input type="checkbox" checked={f.firearmsIssued} onChange={(e) => setF({ ...f, firearmsIssued: e.target.checked })} /> Issued a firearm
          </label>
          <label className="check">
            <input type="checkbox" checked={f.lessLethalIssued} onChange={(e) => setF({ ...f, lessLethalIssued: e.target.checked })} /> Issued less-lethal (Taser, OC, baton)
          </label>
        </div>
        <label>
          Notes
          <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={1000} />
        </label>
        <div>
          <button className="btn primary" disabled={a.busy}>
            Save certification
          </button>
        </div>
      </form>
    </Card>
  );
}

// ─── Training officer: certificates to file ─────────────────────────────────
export function TrainingReview() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState<'pending' | 'filed' | 'returned'>('pending');
  const r = useLoad(() => api.get<any>(`/api/training/submissions?status=${status}`), [status]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<button className="btn link" onClick={() => navigate(at('/training/officers'))}>Training officer</button>}
        title="Certificates to file"
        sub="Certificates officers uploaded. Check each one, put it in the right category, and it counts toward their recertification."
      />
      <div className="seg-ctl" role="tablist" aria-label="Which certificates">
        {(['pending', 'filed', 'returned'] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
            {{ pending: 'To file', filed: 'Filed', returned: 'Sent back' }[k]}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.submissions.length ? (
        <EmptyState icon="check" title={status === 'pending' ? 'Nothing to file' : 'None'} />
      ) : (
        r.data.submissions.map((s: any) => <ReviewCard key={s.id} s={s} cats={r.data.categories} pending={status === 'pending'} onDone={r.reload} />)
      )}
    </div>
  );
}

function ReviewCard({ s, cats, pending, onDone }: { s: any; cats: any[]; pending: boolean; onDone: () => void }) {
  const [f, setF] = useState({ category: s.category ?? '', title: s.title, completedOn: s.completed_on ?? '', hours: s.hours ?? '', acadis: false, separateCert: false, makeupFor: '', result: '' });
  const [note, setNote] = useState('');
  const [returning, setReturning] = useState(false);
  const a = useAction();
  const file = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => (await api.post(`/api/training/submissions/${s.id}/file`, { ...f, hours: Number(f.hours || 0), makeupFor: f.makeupFor || null, result: f.result || null }), onDone()));
  };
  const back = () => a.run(async () => (await api.post(`/api/training/submissions/${s.id}/return`, { note }), onDone()));
  return (
    <Card
      title={
        <>
          {s.officer} · {s.title}
        </>
      }
      actions={
        <a className="btn" href={`/api/training/submissions/${s.id}/file`} target="_blank" rel="noreferrer">
          View certificate
        </a>
      }
    >
      <div className="small muted">
        Sent {d(s.created_at)}
        {s.completed_on ? ` · completed ${d(s.completed_on)}` : ''}
        {s.hours ? ` · ${hrs(s.hours)}` : ''}
        {s.category ? ` · they think: ${catName(cats, s.category)}` : ''}
      </div>
      {s.note && <p className="small">“{s.note}”</p>}
      {!pending ? (
        s.review_note && <p className="small tr-bad">Sent back: {s.review_note}</p>
      ) : (
        <form className="stack sm" onSubmit={file}>
          <ErrorBox error={a.error} />
          <div className="grid-4">
            <label>
              File under
              <select value={f.category} required onChange={(e) => setF({ ...f, category: e.target.value })}>
                <option value="">Choose…</option>
                {cats
                  .filter((c) => !c.archived)
                  .map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Training
              <input value={f.title} required onChange={(e) => setF({ ...f, title: e.target.value })} />
            </label>
            <label>
              Date completed
              <input type="date" value={f.completedOn} required max={localToday()} onChange={(e) => setF({ ...f, completedOn: e.target.value })} />
            </label>
            <label>
              Hours
              <input type="number" min={0} max={400} step={0.5} value={f.hours} required onChange={(e) => setF({ ...f, hours: e.target.value })} />
            </label>
          </div>
          <div className="row gap wrap">
            <label className="check">
              <input type="checkbox" checked={f.acadis} onChange={(e) => setF({ ...f, acadis: e.target.checked })} /> On ACADIS
            </label>
            <label className="check">
              <input type="checkbox" checked={f.separateCert} onChange={(e) => setF({ ...f, separateCert: e.target.checked })} /> Separate certification
            </label>
          </div>
          <div className="row gap wrap">
            <button className="btn primary" disabled={a.busy}>
              File it
            </button>
            {!returning ? (
              <button type="button" className="btn" onClick={() => setReturning(true)}>
                Send back…
              </button>
            ) : (
              <>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why? e.g. can’t read the date" aria-label="Why it’s being sent back" style={{ minWidth: 260 }} />
                <button type="button" className="btn warn" disabled={a.busy || !note.trim()} onClick={back}>
                  Send back
                </button>
              </>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}

// ─── Classes ────────────────────────────────────────────────────────────────
export function TrainingClasses() {
  const { navigate } = useRouter();
  const [adding, setAdding] = useState(false);
  const r = useLoad(() => api.get<any>('/api/training/sessions?past=1'), []);
  const upcoming = (r.data?.sessions ?? []).filter((s: any) => +new Date(s.ends_at) > Date.now() && !s.cancelled_at).reverse();
  const past = (r.data?.sessions ?? []).filter((s: any) => +new Date(s.ends_at) <= Date.now() || s.cancelled_at);
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<button className="btn link" onClick={() => navigate(at('/training/officers'))}>Training officer</button>}
        title="Training classes"
        sub="Schedule a class, see who signed up, and take attendance — everyone who came gets the training on their record."
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => setAdding(!adding)}>
              Schedule a class
            </button>
          )
        }
      />
      {adding && r.data && <NewClass cats={r.data.categories} onDone={() => (setAdding(false), r.reload())} onCancel={() => setAdding(false)} />}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : (
        <>
          <h2 className="tr-h">Coming up</h2>
          {upcoming.length ? <ClassTable rows={upcoming} cats={r.data.categories} /> : <p className="muted small">Nothing scheduled.</p>}
          <h2 className="tr-h">Recent</h2>
          {past.length ? <ClassTable rows={past} cats={r.data.categories} /> : <p className="muted small">None in the last 60 days.</p>}
        </>
      )}
    </div>
  );
}

function ClassTable({ rows, cats }: { rows: any[]; cats: any[] }) {
  const { navigate } = useRouter();
  return (
    <div className="card table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>When</th>
            <th>Class</th>
            <th>Category</th>
            <th>Hours</th>
            <th>Signed up</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="clickable" onClick={() => navigate(at(`/training/classes/${s.id}`))}>
              <td>{dt(s.starts_at)}</td>
              <td>
                <strong>{s.title}</strong>
                {s.location && <div className="small muted">{s.location}</div>}
              </td>
              <td>{catName(cats, s.category)}</td>
              <td>{hrs(s.hours)}</td>
              <td>
                {s.taken}
                {s.seats != null ? ` / ${s.seats}` : ''}
              </td>
              <td>{s.cancelled_at ? <Badge tone="bad">Cancelled</Badge> : s.closed_at ? <Badge tone="ok">Attendance taken</Badge> : +new Date(s.starts_at) <= Date.now() ? <Badge tone="warn">Take attendance</Badge> : <Badge tone="info">Open</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewClass({ cats, onDone, onCancel }: { cats: any[]; onDone: () => void; onCancel: () => void }) {
  const [f, setF] = useState({ title: '', category: '', date: '', start: '08:00', end: '16:00', location: '', hours: '', seats: '', instructor: '', description: '' });
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post('/api/training/sessions', {
        title: f.title,
        category: f.category,
        startsAt: new Date(`${f.date}T${f.start}`).toISOString(),
        endsAt: new Date(`${f.date}T${f.end}`).toISOString(),
        location: f.location || null,
        hours: f.hours ? Number(f.hours) : null,
        seats: f.seats ? Number(f.seats) : null,
        instructor: f.instructor || null,
        description: f.description || null,
      });
      onDone();
    });
  };
  return (
    <Card title="Schedule a class">
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={a.error} />
        <div className="grid-3">
          <label>
            Class
            <input value={f.title} onChange={set('title')} required placeholder="e.g. Annual firearms qualification" />
          </label>
          <label>
            Category
            <select value={f.category} onChange={set('category')} required>
              <option value="">Choose…</option>
              {cats
                .filter((c) => !c.archived)
                .map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Location
            <input value={f.location} onChange={set('location')} />
          </label>
          <label>
            Date
            <input type="date" value={f.date} min={localToday()} onChange={set('date')} required />
          </label>
          <label>
            Starts
            <input type="time" value={f.start} onChange={set('start')} required />
          </label>
          <label>
            Ends
            <input type="time" value={f.end} onChange={set('end')} required />
          </label>
          <label>
            Training hours (if not the class length)
            <input type="number" min={0} max={400} step={0.5} value={f.hours} onChange={set('hours')} />
          </label>
          <label>
            Seats (blank = no limit)
            <input type="number" min={1} value={f.seats} onChange={set('seats')} />
          </label>
          <label>
            Instructor
            <input value={f.instructor} onChange={set('instructor')} />
          </label>
        </div>
        <label>
          What to bring / details
          <textarea rows={2} value={f.description} onChange={set('description')} />
        </label>
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            Schedule
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

export function TrainingClass({ id }: { id: string }) {
  const { navigate } = useRouter();
  const { can } = useAuth();
  const r = useLoad(() => api.get<any>(`/api/training/sessions/${id}`), [id]);
  const cats = useLoad(() => api.get<any>('/api/training/categories'), []);
  const a = useAction();
  const [came, setCame] = useState<string[] | null>(null);
  const v = r.data;
  useEffect(() => {
    if (v && came === null) setCame(v.roster.filter((x: any) => x.status !== 'no_show').map((x: any) => x.user_id));
  }, [v, came]);
  const started = v && +new Date(v.session.starts_at) <= Date.now();
  const signup = (cancel: boolean) => a.run(async () => (await api.post(`/api/training/sessions/${id}/signup`, { cancel }), r.reload()));
  const take = () => a.run(async () => (await api.post(`/api/training/sessions/${id}/attendance`, { attended: came ?? [] }), r.reload()));
  const cancelClass = () => confirm('Cancel this class? Everyone signed up gets a message.') && a.run(async () => (await api.post(`/api/training/sessions/${id}`, { cancel: true }), r.reload()));
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<button className="btn link" onClick={() => navigate(at(can('training.manage') ? '/training/classes' : '/training?tab=classes'))}>Training classes</button>}
        title={v?.session.title ?? 'Class'}
        sub={v ? `${catName(cats.data?.categories ?? [], v.session.category)} · ${dt(v.session.starts_at)} · ${hrs(v.session.hours)}${v.session.location ? ` · ${v.session.location}` : ''}` : undefined}
        actions={
          v &&
          !v.session.cancelled_at &&
          !v.session.closed_at && (
            <>
              {!started &&
                (v.myStatus === 'signed_up' ? (
                  <button className="btn" disabled={a.busy} onClick={() => signup(true)}>
                    Drop
                  </button>
                ) : (
                  <button className="btn primary" disabled={a.busy} onClick={() => signup(false)}>
                    Sign up
                  </button>
                ))}
              {v.canManage && (
                <button className="btn warn" disabled={a.busy} onClick={cancelClass}>
                  Cancel class
                </button>
              )}
            </>
          )
        }
      />
      <ErrorBox error={r.error ?? a.error} />
      {v && (
        <>
          {v.session.cancelled_at && <Notice tone="bad">This class was cancelled.</Notice>}
          {v.session.closed_at && <Notice tone="ok">Attendance was taken; everyone who came has it on their training record.</Notice>}
          {v.myStatus === 'signed_up' && !v.session.closed_at && <Notice tone="ok">You’re signed up.</Notice>}
          {v.session.description && <Card title="Details">{v.session.description}</Card>}
          {v.canManage && (
            <Card title={`Roster (${v.roster.length}${v.session.seats ? ` of ${v.session.seats}` : ''})`}>
              {!v.roster.length ? (
                <p className="small muted">No one has signed up yet.</p>
              ) : (
                <ul className="tr-roster">
                  {v.roster.map((x: any) => (
                    <li key={x.user_id}>
                      {started && !v.session.closed_at ? (
                        <label className="check">
                          <input type="checkbox" checked={!!came?.includes(x.user_id)} onChange={(e) => setCame(e.target.checked ? [...(came ?? []), x.user_id] : (came ?? []).filter((u) => u !== x.user_id))} /> {x.name}
                        </label>
                      ) : (
                        <span>{x.name}</span>
                      )}
                      {x.status !== 'signed_up' && <Badge tone={x.status === 'attended' ? 'ok' : 'warn'}>{x.status === 'attended' ? 'Attended' : 'No-show'}</Badge>}
                    </li>
                  ))}
                </ul>
              )}
              {started && !v.session.closed_at && !v.session.cancelled_at && (
                <div className="row gap" style={{ marginTop: 10 }}>
                  <button className="btn primary" disabled={a.busy} onClick={take}>
                    Save attendance ({came?.length ?? 0} came)
                  </button>
                  <span className="small muted">Everyone checked gets {hrs(v.session.hours)} of training on their record.</span>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── The MRN, filled in ─────────────────────────────────────────────────────
const fiscal = (iso: string) => {
  const [y, m] = iso.split('-').map(Number) as [number, number];
  const start = m >= 7 ? y : y - 1;
  return `${String(start).slice(2)}/${String(start + 1).slice(2)}`;
};

export function TrainingMrn({ id }: { id: string }) {
  const { navigate } = useRouter();
  const { me } = useAuth();
  const r = useLoad(() => api.get<any>(`/api/training/officers/${id}`), [id]);
  const v = r.data;
  const rows = useMemo(() => {
    if (!v || v.progress.status === 'not_set') return [];
    return v.progress.years.map((y: any) => {
      const recs = v.records.filter((x: any) => !x.voided_at && ((x.completed_on >= y.start && x.completed_on < y.end && !x.makeup_for) || (x.makeup_for && x.makeup_for >= y.start && x.makeup_for < y.end)));
      const col = (k: string) => recs.filter((x: any) => x.category === k);
      const other = recs.filter((x: any) => !['dv', 'legal', 'evo', 'mental_health'].includes(x.category) && !x.separate_cert);
      const sep = recs.filter((x: any) => x.separate_cert);
      return { y, dv: col('dv'), legal: col('legal'), evo: col('evo'), other, sep };
    });
  }, [v]);
  const cell = (list: any[], kind: 'dv' | 'legal' | 'other') => {
    if (!list.length) return { dates: '', hours: '', makeup: '', viewed: '' };
    const main = list.filter((x) => !x.makeup_for);
    const mk = list.filter((x) => x.makeup_for);
    return {
      dates: main.slice(0, 2).map((x) => d(x.completed_on)).join('\n'),
      hours: String(list.reduce((s, x) => s + Number(x.hours || 0), 0)),
      makeup: mk.map((x) => d(x.completed_on)).join(', '),
      viewed: kind === 'dv' ? [...new Set(list.map((x) => x.completed_on.slice(0, 4)))].join(', ') : kind === 'legal' ? [...new Set(list.map((x) => fiscal(x.completed_on)))].join(', ') : '',
    };
  };
  return (
    <div className="ops stack mrn-page">
      <div className="row between no-print">
        <button className="btn link" onClick={() => navigate(at(`/training/officers/${id}`))}>
          ‹ Back
        </button>
        <button className="btn primary" onClick={() => print()}>
          Print
        </button>
      </div>
      <ErrorBox error={r.error} />
      {v && v.progress.status !== 'not_set' && (
        <div className="mrn">
          <header>
            <div>South Carolina Criminal Justice Academy</div>
            <h1>MRN — Mandatory Retraining Notification</h1>
            <div className="small">Worksheet from the agency’s training records. Copy onto the official SCCJA form; the agency representative signs the attestation there.</div>
          </header>
          <dl className="mrn-top">
            <div>
              <dt>Agency</dt>
              <dd>{me?.agency.name}</dd>
            </div>
            <div>
              <dt>Certification renewal date</dt>
              <dd>{d(v.progress.period.end)}</dd>
            </div>
            <div>
              <dt>Officer name</dt>
              <dd>{v.officer.name}</dd>
            </div>
            <div>
              <dt>Academy ID #</dt>
              <dd>{v.profile?.academy_id ?? ''}</dd>
            </div>
            <div>
              <dt>Certification class</dt>
              <dd>{v.progress.classLabel}</dd>
            </div>
          </dl>
          {rows.map(({ y, dv, legal, evo, other, sep }: any) => {
            const c = { dv: cell(dv, 'dv'), legal: cell(legal, 'legal'), evo: cell(evo, 'other'), other: cell(other, 'other'), sep: cell(sep, 'other') };
            const R = ({ label, k }: { label: string; k: 'dates' | 'hours' | 'makeup' | 'viewed' }) => (
              <tr>
                <th>{label}</th>
                {(['dv', 'legal', 'evo', 'other', 'sep'] as const).map((x) => (
                  <td key={x}>{c[x][k]}</td>
                ))}
              </tr>
            );
            return (
              <table key={y.n} className="mrn-year">
                <caption>
                  Year {y.n}: {d(y.start)} – {d(y.end)}
                </caption>
                <thead>
                  <tr>
                    <th />
                    <th>Domestic violence</th>
                    <th>Legal</th>
                    <th>EVO</th>
                    <th>In-service</th>
                    <th>Separate certification hours</th>
                  </tr>
                </thead>
                <tbody>
                  <R label="MM/DD/YYYY" k="dates" />
                  <R label="Total hours" k="hours" />
                  <R label="Make up" k="makeup" />
                  <R label="Year viewed" k="viewed" />
                </tbody>
              </table>
            );
          })}
          <table className="mrn-year">
            <caption>Mental health awareness training</caption>
            <tbody>
              <tr>
                <th>MM/DD/YYYY</th>
                <td>{v.progress.mentalHealth ? d(v.progress.mentalHealth.completed_on) : ''}</td>
                <th>Hours</th>
                <td>{v.progress.mentalHealth?.hours ?? ''}</td>
                <th>Year viewed</th>
                <td>{v.progress.mentalHealth ? v.progress.mentalHealth.completed_on.slice(0, 4) : ''}</td>
              </tr>
            </tbody>
          </table>
          <p className="small">
            Total in-service hours, 3 years: <strong>{v.progress.hours.done}</strong>
            {v.progress.hours.need ? ` (needs ${v.progress.hours.need})` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

/** Categories: the MRN's plus the agency's own; which ones the agency requires every year. */
function CategoriesCard({ onClose }: { onClose: () => void }) {
  const r = useLoad(() => api.get<any>('/api/training/categories'), []);
  const a = useAction();
  const [name, setName] = useState('');
  const save = (c: any, patch: Record<string, unknown>) => a.run(async () => (await api.post('/api/training/categories', { key: c.key, name: c.name, annual: c.annual, archived: c.archived, ...patch }), r.reload()));
  const add = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => (await api.post('/api/training/categories', { name, annual: false }), setName(''), r.reload()));
  };
  return (
    <Card title="Training categories" actions={<button className="btn" onClick={onClose}>Done</button>}>
      <p className="small muted">
        The state’s yearly topics come from each officer’s certification class (Class 1: DV, Legal and EVO). Check “Every year” for anything else your agency requires yearly — firearms and less-lethal only count for officers issued them.
      </p>
      <ErrorBox error={r.error ?? a.error} />
      <ul className="tr-cats">
        {(r.data?.categories ?? []).map((c: any) => (
          <li key={c.key} className={c.archived ? 'muted' : ''}>
            <span>
              {c.name} {c.mrn && <Badge tone="info">MRN</Badge>}
            </span>
            <label className="check">
              <input type="checkbox" checked={c.annual} disabled={a.busy || c.archived} onChange={(e) => save(c, { annual: e.target.checked })} /> Every year
            </label>
            {!c.mrn && (
              <button type="button" className="btn link small" disabled={a.busy} onClick={() => save(c, { archived: !c.archived })}>
                {c.archived ? 'Restore' : 'Remove'}
              </button>
            )}
          </li>
        ))}
      </ul>
      <form className="row gap" onSubmit={add}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category, e.g. CPR / First aid" aria-label="New category" maxLength={80} />
        <button className="btn" disabled={a.busy || !name.trim()}>
          Add
        </button>
      </form>
    </Card>
  );
}

// ─── Firearms qualification ─────────────────────────────────────────────────
const WEAPONS = ['Handgun', 'Patrol rifle', 'Shotgun', 'Off-duty / backup handgun', 'Less-lethal launcher', 'Precision rifle'];
function FirearmsCard({ v, id, onSaved }: { v: any; id: string | null; onSaved: () => void }) {
  const fa = v.firearms;
  const [open, setOpen] = useState(false);
  const blank = { completedOn: localToday(), weaponType: 'Handgun', make: '', model: '', serial: '', caliber: '', course: 'Day qualification', score: '', attempts: '1', passed: true, instructor: '', hours: '4', notes: '' };
  const [f, setF] = useState(blank);
  const a = useAction();
  if (!fa) return null;
  const last = fa.last;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post('/api/training/records', {
        userIds: [id], category: 'firearms', title: `${f.weaponType} qualification — ${f.course}`, completedOn: f.completedOn, hours: Number(f.hours || 0),
        result: `${f.passed ? 'Qualified' : 'Did not qualify'}${f.score ? ` ${f.score}%` : ''}`, instructor: f.instructor || null, notes: f.notes || null,
        details: { weaponType: f.weaponType, make: f.make, model: f.model, serial: f.serial, caliber: f.caliber, course: f.course, score: f.score === '' ? null : Number(f.score), attempts: Number(f.attempts || 1), passed: f.passed },
      });
      setF(blank);
      setOpen(false);
      onSaved();
    });
  };
  const weapon = (d: any) => [d?.weaponType, [d?.make, d?.model].filter(Boolean).join(' '), d?.caliber, d?.serial && `SN ${d.serial}`].filter(Boolean).join(' · ');
  return (
    <Card
      title="Firearms qualification"
      actions={
        v.canManage &&
        id && (
          <button className="btn small primary" onClick={() => setOpen(!open)}>
            {open ? 'Close' : 'Record a qualification'}
          </button>
        )
      }
    >
      <div className="stack sm">
        {!fa.issued && <Notice>Not issued a firearm — qualification isn’t required.</Notice>}
        <div className="fq-status">
          {last ? (
            <>
              <Badge tone={fa.overdue ? 'bad' : fa.daysLeft != null && fa.daysLeft <= 60 ? 'warn' : 'ok'}>{fa.overdue ? 'Qualification overdue' : 'Qualified'}</Badge>
              <span>
                Last qualified <strong>{d(last.completed_on)}</strong>
                {last.details?.score != null ? ` · ${last.details.score}%` : ''} · next due <strong>{d(fa.nextDue)}</strong>
                {fa.daysLeft != null && !fa.overdue ? ` (${fa.daysLeft} days)` : ''}
              </span>
            </>
          ) : (
            <>
              <Badge tone={fa.issued ? 'bad' : 'neutral'}>Not qualified</Badge>
              <span className="small muted">No passing qualification on file.</span>
            </>
          )}
        </div>
        <QualAverages s={fa.stats} />
        {open && (
          <form className="cw-form stack sm" onSubmit={save}>
            <ErrorBox error={a.error} />
            <div className="grid-4">
              <label>
                Date
                <input type="date" value={f.completedOn} max={localToday()} onChange={set('completedOn')} required />
              </label>
              <label>
                Weapon
                <select value={f.weaponType} onChange={set('weaponType')}>
                  {WEAPONS.map((w) => (
                    <option key={w}>{w}</option>
                  ))}
                </select>
              </label>
              <label>
                Make
                <input value={f.make} onChange={set('make')} placeholder="Glock" />
              </label>
              <label>
                Model
                <input value={f.model} onChange={set('model')} placeholder="45" />
              </label>
              <label>
                Serial #
                <input value={f.serial} onChange={set('serial')} />
              </label>
              <label>
                Caliber
                <input value={f.caliber} onChange={set('caliber')} placeholder="9mm" />
              </label>
              <label>
                Course
                <select value={f.course} onChange={set('course')}>
                  {QUAL_COURSES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                Score (%)
                <input type="number" min={0} max={300} value={f.score} onChange={set('score')} />
              </label>
              <label>
                Attempts
                <input type="number" min={1} max={10} value={f.attempts} onChange={set('attempts')} />
              </label>
              <label>
                Range officer
                <input value={f.instructor} onChange={set('instructor')} />
              </label>
              <label>
                Hours
                <input type="number" min={0} step={0.5} value={f.hours} onChange={set('hours')} />
              </label>
            </div>
            <label className="check">
              <input type="checkbox" checked={f.passed} onChange={(e) => setF({ ...f, passed: e.target.checked })} /> Qualified (passed)
            </label>
            <label>
              Notes
              <input value={f.notes} onChange={set('notes')} />
            </label>
            <div>
              <button className="btn primary" disabled={a.busy}>
                Save qualification
              </button>
            </div>
          </form>
        )}
        {fa.history.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Weapon</th>
                  <th>Course</th>
                  <th>Score</th>
                  <th>Result</th>
                  <th>Range officer</th>
                </tr>
              </thead>
              <tbody>
                {fa.history.map((q: any) => (
                  <tr key={q.id}>
                    <td>{d(q.completed_on)}</td>
                    <td>{weapon(q.details) || q.title}</td>
                    <td>{q.details?.course ?? ''}</td>
                    <td>{q.details?.score != null ? `${q.details.score}%` : ''}</td>
                    <td>{q.details?.passed === false ? <Badge tone="bad">Did not qualify</Badge> : <Badge tone="ok">Qualified{q.details?.attempts > 1 ? ` (${q.details.attempts} tries)` : ''}</Badge>}</td>
                    <td>{q.instructor ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Training officer: log a training roster (a class, and everyone who was there) ───────────────
const hoursBetween = (a: string, b: string) => {
  if (!a || !b) return '';
  const [h1, m1] = a.split(':').map(Number);
  const [h2, m2] = b.split(':').map(Number);
  let m = h2! * 60 + m2! - (h1! * 60 + m1!);
  if (m <= 0) m += 24 * 60;
  return String(Math.round((m / 60) * 4) / 4);
};

/** Search, filters (cert level, supervisor, shift, rank, division) and A–Z / Z–A for a list of officers. */
function useOfficerFilter(people: any[]) {
  const [q, setQ] = useState('');
  const none = { certClass: '', supervisor: '', shift: '', rank: '', division: '' };
  const [flt, setFlt] = useState(none);
  const [sort, setSort] = useState<'az' | 'za'>('az');
  const opts = (k: string) => [...new Set(people.map((p) => p[k]).filter(Boolean))].sort((x: any, y: any) => String(x).localeCompare(String(y))) as string[];
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return people
      .filter((p) => (!flt.certClass || p.certClass === flt.certClass) && (!flt.supervisor || p.supervisor === flt.supervisor) && (!flt.shift || p.shift === flt.shift) && (!flt.rank || p.rank === flt.rank) && (!flt.division || p.division === flt.division))
      .filter((p) => !s || [p.name, p.badge, p.rank, p.shift, p.division, p.supervisor].some((x) => x && String(x).toLowerCase().includes(s)))
      .sort((x, y) => (sort === 'az' ? 1 : -1) * x.sortName.localeCompare(y.sortName));
  }, [people, q, flt, sort]);
  const filters: [keyof typeof flt, string, string[]][] = [
    ['certClass', 'Cert level', CERT_CLASS_KEYS as unknown as string[]],
    ['supervisor', 'Supervisor', opts('supervisor')],
    ['shift', 'Shift', opts('shift')],
    ['rank', 'Rank', opts('rank')],
    ['division', 'Division', opts('division')],
  ];
  const bar = (
    <div className="tr-rost-filters">
      <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names…" aria-label="Search officers" />
      {filters.map(([k, label, list]) => (
        <select key={k} value={flt[k]} onChange={(e) => setFlt({ ...flt, [k]: e.target.value })} aria-label={`Filter by ${label.toLowerCase()}`}>
          <option value="">{`Any ${label.toLowerCase()}`}</option>
          {list.map((v) => (
            <option key={v} value={v}>
              {k === 'certClass' ? CERT_CLASSES[v as keyof typeof CERT_CLASSES]?.label ?? v : v}
            </option>
          ))}
        </select>
      ))}
      <div className="seg-ctl" role="group" aria-label="Sort">
        <button type="button" className={sort === 'az' ? 'on' : ''} onClick={() => setSort('az')}>
          A–Z
        </button>
        <button type="button" className={sort === 'za' ? 'on' : ''} onClick={() => setSort('za')}>
          Z–A
        </button>
      </div>
      {Object.values(flt).some(Boolean) || q ? (
        <button type="button" className="btn link small" onClick={() => (setFlt(none), setQ(''))}>
          Clear filters
        </button>
      ) : null}
    </div>
  );
  return { shown, bar };
}

export function TrainingRoster() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/training/roster-people'));
  const a = useAction();
  const [f, setF] = useState({ completedOn: localToday(), startTime: '', endTime: '', location: '', hours: '', category: '', title: '', notes: '', acadis: false });
  const [instructors, setInstructors] = useState<string[]>(['']);
  const [hoursTouched, setHoursTouched] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [saved, setSaved] = useState<number | null>(null);
  const preset: Record<string, string> = { firearms: 'Annual firearms qualification', less_lethal: 'Less-lethal weapons in-service', use_of_force: 'Use of force policy review', evo: 'EVO in-service', dv: 'Domestic violence in-service', legal: 'Legal update', mental_health: 'Mental health awareness', ground_defense: 'Ground defense' };
  const people: any[] = r.data?.officers ?? [];
  const { shown, bar } = useOfficerFilter(people);
  const setTime = (k: 'startTime' | 'endTime') => (e: { target: { value: string } }) => {
    const n = { ...f, [k]: e.target.value };
    if (!hoursTouched) n.hours = hoursBetween(n.startTime, n.endTime);
    setF(n);
  };
  const allShown = shown.length > 0 && shown.every((p) => picked.includes(p.id));
  const toggleAll = () => setPicked(allShown ? picked.filter((id) => !shown.some((p) => p.id === id)) : [...new Set([...picked, ...shown.map((p) => p.id)])]);
  const toggle = (id: string) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const save = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      const inst = instructors.map((s) => s.trim()).filter(Boolean);
      await api.post('/api/training/records', {
        userIds: picked,
        category: f.category,
        title: f.title,
        completedOn: f.completedOn,
        hours: Number(f.hours || 0),
        location: f.location || null,
        instructor: inst.join(', ') || null,
        notes: f.notes || null,
        acadis: f.acadis,
        details: { startTime: f.startTime || undefined, endTime: f.endTime || undefined, instructors: inst, roster: true },
      });
      setSaved(picked.length);
      setPicked([]);
      window.scrollTo(0, 0);
    });
  };
  return (
    <div className="ops stack">
      <PageHeader crumb={<button className="btn link" onClick={() => navigate(at('/training/officers'))}>Training officer</button>} title="Log a training roster" sub="Fill in the class, check off everyone who attended, and save. It goes on each officer’s training record." />
      {saved != null && (
        <Notice tone="ok">
          Saved for {saved} officer{saved === 1 ? '' : 's'}. You can log another roster below.
        </Notice>
      )}
      <form className="stack" onSubmit={save}>
        <Card title="The training">
          <div className="grid-3">
            <label>
              Date
              <input type="date" value={f.completedOn} max={localToday()} onChange={(e) => setF({ ...f, completedOn: e.target.value })} required />
            </label>
            <label>
              Start time
              <input type="time" value={f.startTime} onChange={setTime('startTime')} />
            </label>
            <label>
              End time
              <input type="time" value={f.endTime} onChange={setTime('endTime')} />
            </label>
            <label>
              Hours
              <input type="number" min={0} max={400} step={0.25} value={f.hours} onChange={(e) => (setHoursTouched(true), setF({ ...f, hours: e.target.value }))} required />
            </label>
            <label>
              Type of training
              <select value={f.category} required onChange={(e) => setF({ ...f, category: e.target.value, title: f.title || preset[e.target.value] || '' })}>
                <option value="">Choose…</option>
                {(r.data?.categories ?? [])
                  .filter((c: any) => !c.archived)
                  .map((c: any) => (
                    <option key={c.key} value={c.key}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Class name
              <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={200} placeholder="e.g. Fall firearms qualification" />
            </label>
            <label>
              Location
              <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} maxLength={200} placeholder="e.g. Range" />
            </label>
          </div>
          <div className="stack sm">
            <span>Instructor(s)</span>
            {instructors.map((s, i) => (
              <div key={i} className="row gap-sm">
                <input value={s} onChange={(e) => setInstructors(instructors.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`Instructor ${i + 1}`} placeholder="Name" maxLength={80} />
                {instructors.length > 1 && (
                  <button type="button" className="btn link small" onClick={() => setInstructors(instructors.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn small" style={{ alignSelf: 'flex-start' }} onClick={() => setInstructors([...instructors, ''])}>
              + Another instructor
            </button>
          </div>
          <label>
            Notes (optional)
            <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={2000} />
          </label>
          <label className="check">
            <input type="checkbox" checked={f.acadis} onChange={(e) => setF({ ...f, acadis: e.target.checked })} /> Entered on ACADIS
          </label>
        </Card>
        <Card title={`Who attended (${picked.length} checked)`}>
          {bar}
          {r.error ? (
            <ErrorBox error={r.error} />
          ) : !r.data ? (
            <Spinner />
          ) : !shown.length ? (
            <EmptyState icon="people" title="No one matches" />
          ) : (
            <div className="table-wrap">
              <table className="table tr-rost">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={allShown} onChange={toggleAll} aria-label="Check everyone shown" />
                    </th>
                    <th>Name</th>
                    <th>Rank</th>
                    <th>Cert level</th>
                    <th>Shift</th>
                    <th>Supervisor</th>
                    <th>Division</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => (
                    <tr key={p.id} className={`clickable${picked.includes(p.id) ? ' on' : ''}`} onClick={() => toggle(p.id)}>
                      <td>
                        <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} onClick={(e) => e.stopPropagation()} aria-label={`Attended: ${p.name}`} />
                      </td>
                      <td>
                        <strong>{p.name}</strong>
                        {p.badge && <div className="small muted">{p.badge}</div>}
                      </td>
                      <td>{p.rank ?? '—'}</td>
                      <td>{p.classLabel ?? '—'}</td>
                      <td>{p.shift ?? '—'}</td>
                      <td>{p.supervisor ?? '—'}</td>
                      <td>{p.division ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <ErrorBox error={a.error} />
        <div className="row gap tr-rost-save">
          <button className="btn primary" disabled={a.busy || !picked.length}>
            {a.busy ? 'Saving…' : picked.length ? `Save roster (${picked.length} officer${picked.length === 1 ? '' : 's'})` : 'Check who attended'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(at('/training/officers'))}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Firearms: averages and the "scores dropping" warning ───────────────────
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);
const LIGHT: Record<string, string> = { day: 'Day', night: 'Night' };
function declineText(dd: any) {
  return `${LIGHT[dd.light]} scores dropped two quals in a row: ${dd.scores.join('% → ')}%`;
}
function QualAverages({ s }: { s: any }) {
  if (!s) return null;
  return (
    <div className="stack sm">
      <div className="fq-avgs">
        {(['day', 'night', 'overall'] as const).map((k) => (
          <div key={k} className="fq-avg">
            <span className="small muted">{k === 'overall' ? 'Overall average' : `${LIGHT[k]} average`}</span>
            <strong>{pct(s[k].avg)}</strong>
            <span className="small muted">
              {s[k].count} qual{s[k].count === 1 ? '' : 's'}
            </span>
          </div>
        ))}
      </div>
      {s.declines.map((dd: any) => (
        <Notice key={dd.light} tone="warn">
          ⚠ {declineText(dd)} ({dd.dates.map((x: string) => d(x)).join(', ')}).
        </Notice>
      ))}
    </div>
  );
}
function QualWarn({ fq }: { fq: any }) {
  if (!fq?.declines?.length) return null;
  return (
    <Badge tone="warn">
      <span title={fq.declines.map(declineText).join('\n')}>⚠ Scores dropping</span>
    </Badge>
  );
}

// ─── Training officer: a day at the range, logged like a roster ─────────────
export function QualRoster() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/training/roster-people'));
  const a = useAction();
  const [f, setF] = useState({ completedOn: localToday(), startTime: '', endTime: '', location: '', hours: '', course: 'Day qualification', weaponType: 'Handgun', passing: '80', notes: '', acadis: false });
  const [instructors, setInstructors] = useState<string[]>(['']);
  const [hoursTouched, setHoursTouched] = useState(false);
  const [rows, setRows] = useState<Record<string, { score: string; attempts: string; passed: boolean; serial: string; make: string; model: string; caliber: string; touched: boolean }>>({});
  const [saved, setSaved] = useState<number | null>(null);
  const people: any[] = r.data?.officers ?? [];
  const { shown, bar } = useOfficerFilter(people);
  const picked = Object.keys(rows);
  const passing = Number(f.passing) || 0;
  const gun = (p: any, w: string) => p.guns?.[w] ?? {};
  const blankRow = (p: any) => {
    const g = gun(p, f.weaponType);
    return { score: '', attempts: '1', passed: true, serial: g.serial ?? '', make: g.make ?? '', model: g.model ?? '', caliber: g.caliber ?? '', touched: false };
  };
  const toggle = (p: any) => setRows((rs) => {
    const n = { ...rs };
    if (n[p.id]) delete n[p.id];
    else n[p.id] = blankRow(p);
    return n;
  });
  const allShown = shown.length > 0 && shown.every((p) => rows[p.id]);
  const toggleAll = () => setRows((rs) => {
    const n = { ...rs };
    if (allShown) shown.forEach((p) => delete n[p.id]);
    else shown.forEach((p) => (n[p.id] ??= blankRow(p)));
    return n;
  });
  const setRow = (id: string, patch: Partial<(typeof rows)[string]>) =>
    setRows((rs) => {
      const cur = { ...rs[id]!, ...patch };
      // Pass/fail follows the passing score until it's set by hand.
      if ('score' in patch && !cur.touched && cur.score !== '') cur.passed = Number(cur.score) >= passing;
      return { ...rs, [id]: cur };
    });
  const setTime = (k: 'startTime' | 'endTime') => (e: { target: { value: string } }) => {
    const n = { ...f, [k]: e.target.value };
    if (!hoursTouched) n.hours = hoursBetween(n.startTime, n.endTime);
    setF(n);
  };
  const changeWeapon = (w: string) => {
    setF({ ...f, weaponType: w });
    setRows((rs) => Object.fromEntries(Object.entries(rs).map(([id, row]) => {
      const g = gun(people.find((p) => p.id === id), w);
      return [id, { ...row, serial: g.serial ?? '', make: g.make ?? '', model: g.model ?? '', caliber: g.caliber ?? '' }];
    })));
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post('/api/training/quals', {
        completedOn: f.completedOn,
        startTime: f.startTime || undefined,
        endTime: f.endTime || undefined,
        hours: Number(f.hours || 0),
        location: f.location || null,
        course: f.course,
        weaponType: f.weaponType,
        instructors: instructors.map((s) => s.trim()).filter(Boolean),
        notes: f.notes || null,
        acadis: f.acadis,
        rows: Object.entries(rows).map(([userId, x]) => ({ userId, score: x.score === '' ? null : Number(x.score), attempts: Number(x.attempts || 1), passed: x.passed, serial: x.serial, make: x.make, model: x.model, caliber: x.caliber })),
      });
      setSaved(picked.length);
      setRows({});
      r.reload();
      window.scrollTo(0, 0);
    });
  };
  const missingScore = Object.values(rows).filter((x) => x.score === '').length;
  return (
    <div className="ops stack">
      <PageHeader crumb={<button className="btn link" onClick={() => navigate(at('/training/officers'))}>Training officer</button>} title="Log firearms qualification" sub="Fill in the range day, check off who shot, and enter each officer’s score. It goes on each officer’s firearms record." />
      {saved != null && (
        <Notice tone="ok">
          Saved {saved} qualification{saved === 1 ? '' : 's'}. You can log another relay below.
        </Notice>
      )}
      <form className="stack" onSubmit={save}>
        <Card title="The qualification">
          <div className="grid-3">
            <label>
              Date
              <input type="date" value={f.completedOn} max={localToday()} onChange={(e) => setF({ ...f, completedOn: e.target.value })} required />
            </label>
            <label>
              Start time
              <input type="time" value={f.startTime} onChange={setTime('startTime')} />
            </label>
            <label>
              End time
              <input type="time" value={f.endTime} onChange={setTime('endTime')} />
            </label>
            <label>
              Hours
              <input type="number" min={0} max={400} step={0.25} value={f.hours} onChange={(e) => (setHoursTouched(true), setF({ ...f, hours: e.target.value }))} required />
            </label>
            <label>
              Course
              <select value={f.course} onChange={(e) => setF({ ...f, course: e.target.value })}>
                {QUAL_COURSES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label>
              Weapon
              <select value={f.weaponType} onChange={(e) => changeWeapon(e.target.value)}>
                {WEAPONS.map((w) => (
                  <option key={w}>{w}</option>
                ))}
              </select>
            </label>
            <label>
              Location
              <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} maxLength={200} placeholder="e.g. County range" />
            </label>
            <label>
              Passing score (%)
              <input type="number" min={0} max={300} value={f.passing} onChange={(e) => setF({ ...f, passing: e.target.value })} />
            </label>
          </div>
          <div className="stack sm">
            <span>Range officer(s) / instructor(s)</span>
            {instructors.map((s, i) => (
              <div key={i} className="row gap-sm">
                <input value={s} onChange={(e) => setInstructors(instructors.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`Instructor ${i + 1}`} placeholder="Name" maxLength={80} />
                {instructors.length > 1 && (
                  <button type="button" className="btn link small" onClick={() => setInstructors(instructors.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn small" style={{ alignSelf: 'flex-start' }} onClick={() => setInstructors([...instructors, ''])}>
              + Another instructor
            </button>
          </div>
          <label className="check">
            <input type="checkbox" checked={f.acadis} onChange={(e) => setF({ ...f, acadis: e.target.checked })} /> Entered on ACADIS
          </label>
        </Card>
        <Card title={`Who shot (${picked.length} checked)`}>
          {bar}
          {r.error ? (
            <ErrorBox error={r.error} />
          ) : !r.data ? (
            <Spinner />
          ) : !shown.length ? (
            <EmptyState icon="people" title="No one matches" />
          ) : (
            <div className="table-wrap">
              <table className="table tr-rost fq-rost">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={allShown} onChange={toggleAll} aria-label="Check everyone shown" />
                    </th>
                    <th>Name</th>
                    <th>Day avg</th>
                    <th>Night avg</th>
                    <th>Overall</th>
                    <th>Score (%)</th>
                    <th>Tries</th>
                    <th>Qualified</th>
                    <th>Serial #</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => {
                    const x = rows[p.id];
                    return (
                      <tr key={p.id} className={x ? 'on' : ''}>
                        <td>
                          <input type="checkbox" checked={!!x} onChange={() => toggle(p)} aria-label={`Shot: ${p.name}`} />
                        </td>
                        <td>
                          <strong>{p.name}</strong>
                          <div className="small muted">{[p.badge, p.shift].filter(Boolean).join(' · ')}</div>
                          <QualWarn fq={p.fq} />
                        </td>
                        <td>{pct(p.fq?.day)}</td>
                        <td>{pct(p.fq?.night)}</td>
                        <td>{pct(p.fq?.overall)}</td>
                        {x ? (
                          <>
                            <td>
                              <input className="fq-in" type="number" min={0} max={300} value={x.score} onChange={(e) => setRow(p.id, { score: e.target.value })} aria-label={`Score for ${p.name}`} />
                            </td>
                            <td>
                              <input className="fq-in sm" type="number" min={1} max={10} value={x.attempts} onChange={(e) => setRow(p.id, { attempts: e.target.value })} aria-label={`Tries for ${p.name}`} />
                            </td>
                            <td>
                              <label className="check">
                                <input type="checkbox" checked={x.passed} onChange={(e) => setRow(p.id, { passed: e.target.checked, touched: true })} aria-label={`Qualified: ${p.name}`} />
                                <span className={x.passed ? '' : 'tr-bad'}>{x.passed ? 'Yes' : 'No'}</span>
                              </label>
                            </td>
                            <td>
                              <input className="fq-in wide" value={x.serial} onChange={(e) => setRow(p.id, { serial: e.target.value })} aria-label={`Serial for ${p.name}`} placeholder={[x.make, x.model].filter(Boolean).join(' ') || 'Serial'} />
                            </td>
                          </>
                        ) : (
                          <td colSpan={4} className="small muted clickable" onClick={() => toggle(p)}>
                            Check to enter a score
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <ErrorBox error={a.error} />
        <div className="row gap tr-rost-save">
          <button className="btn primary" disabled={a.busy || !picked.length}>
            {a.busy ? 'Saving…' : picked.length ? `Save ${picked.length} qualification${picked.length === 1 ? '' : 's'}` : 'Check who shot'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(at('/training/officers'))}>
            Cancel
          </button>
          {missingScore > 0 && picked.length > 0 && <span className="small muted">{missingScore} without a score — that’s OK if your course is pass/fail.</span>}
        </div>
      </form>
    </div>
  );
}
